/**
 * Detects speech-to-text "hallucinations": phrases Whisper-style models emit on
 * silence or background noise (YouTube outro lines, subscribe prompts, lone
 * fillers). Shared by the browser (so the bubble is never shown) and the server
 * (so the line is never saved as the student's speech or graded).
 */

const ARTIFACT_PHRASES = [
  'thanks for watching',
  'thank you for watching',
  'thank you so much for watching',
  'thanks for listening',
  'please subscribe',
  'like and subscribe',
  'subscribe to my channel',
  'subscribe to the channel',
  'see you in the next video',
  'see you next time',
  'learn english for free',
  'www.',
  'http://',
  'https://',
  '.com',
  'engvid',
  'transcribed by',
  'subtitles by',
  'amara.org',
  'copyright',
  'legendas pela comunidade',
  'obrigado por assistir',
  'obrigada por assistir',
  'inscreva-se',
  'inscreva se',
];

/** Lone fillers that carry no student speech when they are the whole utterance. */
const LONE_FILLERS = new Set([
  'nevermind',
  'never mind',
  'you',
  'bye',
  'thank you',
  'thanks',
  'okay',
  'ok',
  'hmm',
  'mm',
  'uh',
  'um',
  'oh',
  'ah',
  'so',
  'yeah',
  'yes',
  'no',
  'the',
  'a',
  '.',
  '...',
  'música',
  'music',
  '[music]',
  '[blank_audio]',
  '[silence]',
  '[inaudible]',
]);

function normalise(text: string): string {
  return text
    .toLowerCase()
    .replace(/[‘’“”"']/g, '')
    .replace(/[!?.,;:()\-–—]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * True when the transcript is empty, a known speech-to-text artifact, or a lone
 * filler word — i.e. nothing the student actually said.
 */
export function isLikelyTranscriptionArtifact(text: string | null | undefined): boolean {
  if (!text) return true;
  const normalised = normalise(text);
  if (!normalised) return true;
  if (LONE_FILLERS.has(normalised)) return true;
  // A single short token (e.g. "a", "I") is noise, not an answer.
  if (normalised.length <= 2) return true;
  return ARTIFACT_PHRASES.some((phrase) => normalised.includes(phrase));
}

/** Removes a surrounding "[ ... ]" or "( ... )" wrapper used by some STT models for non-speech tags. */
export function isNonSpeechTag(text: string): boolean {
  const trimmed = text.trim();
  return /^[[(][^\])]*[\])]$/.test(trimmed);
}

/** Counts words in the way the evaluator does (whitespace-separated tokens with at least one letter). */
export function countSpokenWords(text: string): number {
  return text
    .split(/\s+/)
    .filter((token) => /\p{L}/u.test(token)).length;
}
